import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { KeyConstants } from "../../Utils/keyConstants";
import { isEmptyArray } from "../../Utils/util";
import { focusNext } from "../../Utils/focusUtils";
import AutoComplete from "./AutoComplete";

// ── Inline dropdown suggestion styles (injected once) ────────────────────────
const INLINE_LOOKUP_STYLES = `
  .itb-wrap { position: relative; }
  .itb-dropdown {
    position: absolute; top: calc(100% + 2px); left: 0; right: 0;
    background: #fff;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    z-index: 1000;
    max-height: 220px;
    overflow-y: auto;
    animation: itbFadeIn .13s ease;
  }
      /* Portal variant — positioned via inline top/left/width (JS-computed), escapes overflow:hidden/auto ancestors like .mobile-sheet-body */
  .itb-dropdown-portal {
    position: absolute;
    z-index: 9999;
    background: #fff;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
    max-height: 220px;
    overflow-y: auto;
    animation: itbFadeIn .13s ease;
  }
  @keyframes itbFadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .itb-dropdown-item {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 14px;
    cursor: pointer;
    border-bottom: 1px solid #f3f4f6;
    transition: background .1s;
  }
  .itb-dropdown-item:last-child { border-bottom: none; }
  .itb-dropdown-item:hover, .itb-dropdown-item--active { background: #eff6ff; }
  .itb-dropdown-item__code {
    font-size: 12.5px; font-weight: 700; color: #1d4ed8;
    background: #dbeafe; border-radius: 4px; padding: 2px 7px;
    font-family: monospace; white-space: nowrap; flex-shrink: 0;
  }
  .itb-dropdown-item__desc {
    font-size: 12.5px; color: #374151;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .itb-dropdown-loading {
    padding: 10px 14px; font-size: 12.5px; color: #6b7280; font-style: italic;
  }
  .itb-dropdown-empty {
    padding: 10px 14px; font-size: 12.5px; color: #9ca3af; text-align: center;
  }
`;

let _stylesInjected = false;
const injectStyles = () => {
  if (_stylesInjected) return;
  const el = document.createElement("style");
  el.textContent = INLINE_LOOKUP_STYLES;
  document.head.appendChild(el);
  _stylesInjected = true;
};

/**
 * InputTextBox — enhanced with inline lookup dropdown.
 *
 * New props (all optional — 100 % backwards-compatible):
 *   lookupList          {Array<{code, description}>}  items to show in dropdown
 *   onLookupSelect      {(item) => void}               called when user picks an item
 *   showLookupDropdown  {boolean}                      whether the dropdown is visible
 *   onHideLookupDropdown {() => void}                  called when dropdown should close
 *   isLookupLoading     {boolean}                      shows "Searching…" placeholder
 */
export default function InputTextBox(props) {
  const {
    lookupList          = [],
    onLookupSelect,
    showLookupDropdown  = false,
    onHideLookupDropdown,
    isLookupLoading     = false,
    // inputRef — external ref forwarded to the <input> element so parent
    // components can programmatically focus this field (e.g. Enter-key navigation)
    inputRef: externalInputRef,
  } = props;

  const internalInputRef = useRef(null);
  // Prefer the externally-provided ref; fall back to the internal one
  const resolvedInputRef = externalInputRef || internalInputRef;

  const [suggestions, setSuggestions] = useState(props.suggestions);
  const [activeIdx, setActiveIdx]     = useState(-1);
   const [dropdownPos, setDropdownPos] = useState(null);
  const wrapRef                       = useRef(null);

  injectStyles();

  useEffect(() => {
    setSuggestions(props.suggestions);
  }, [props.suggestions]);

  // Reset keyboard cursor whenever list changes
 // Changed now //useEffect(() => { setActiveIdx(-1); }, [lookupList]);
 useEffect(() => { setActiveIdx(lookupList.length > 0 ? 0 : -1); }, [lookupList]);


   // Track wrap position so the portal dropdown can be placed under it
  // (bypasses overflow:hidden/auto ancestors, e.g. .mobile-sheet-body)
  useEffect(() => {
    if (!showLookupDropdown) return;
    const updatePos = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [showLookupDropdown, lookupList]);

  // Close dropdown on outside click
useEffect(() => {
    if (!showLookupDropdown) return;
    const handler = (e) => {
      if (
        wrapRef.current && !wrapRef.current.contains(e.target) &&
        !e.target.closest(".itb-dropdown-portal")
      ) {
        if (onHideLookupDropdown) onHideLookupDropdown();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLookupDropdown, onHideLookupDropdown]);

  const onKeyDown = (e) => {
    // Navigate the inline dropdown with arrow keys + Enter + Escape
    if (showLookupDropdown && lookupList.length > 0) {
      if (e.keyCode === 40) { // Arrow down
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, lookupList.length - 1));
        return;
      }
      if (e.keyCode === 38) { // Arrow up
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.keyCode === KeyConstants.ENTER_KEY && activeIdx >= 0) {
        e.preventDefault();
        handleSelect(lookupList[activeIdx]);
        return;
      }
      if (e.keyCode === 27) { // Escape
        if (onHideLookupDropdown) onHideLookupDropdown();
        return;
      }
    }
	
    //if (e.keyCode === KeyConstants.ENTER_KEY) {
     //if (props.enterKeyHandler) props.enterKeyHandler(e);
    //}
  };

  const onChange = (e) => {
    if (props.onChange) props.onChange(e);
  };

  // const handleSelect = (item) => {
  //   if (onLookupSelect) onLookupSelect(item);
  //   if (onHideLookupDropdown) onHideLookupDropdown();
  //   setActiveIdx(-1);
  // };

  const handleSelect = (item) => {
  if (onLookupSelect) onLookupSelect(item);
  if (onHideLookupDropdown) onHideLookupDropdown();
  setActiveIdx(-1);

  // move focus forward, same as a normal Enter-key advance
  if (props.enterKeyHandler) {
    props.enterKeyHandler();
  } else {
    focusNext(resolvedInputRef.current);
  }
};

  // Legacy AutoComplete clickHandler (unchanged behaviour)
  const clickHandler = (e, value) => {
   
    if (props.clickHandler) props.clickHandler(e);
  };

  const showInlineDropdown = showLookupDropdown && (isLookupLoading || lookupList.length > 0);

  return (
    <>
      <div className="input-effect itb-wrap" ref={wrapRef}>
        <input
          ref={resolvedInputRef}
          className="effect-17"
          type="text"
          required
          placeholder={props.placeholder}
          value={props.value ? props.value : ""}
          onChange={(e) => onChange(e)}
          onKeyDown={onKeyDown}
          disabled={props.disable}
          name={props.name}
          readOnly={props.readOnly}
          autoComplete="off"
        />
        <label className="input-effect-label">{props.label}</label>
        <span className="focus-border"></span>

        {/* Legacy AutoComplete (unchanged) */}
        {!isEmptyArray(suggestions) ? (
          <AutoComplete
            clickHandler={(e) => clickHandler(e, props.value)}
            suggestions={suggestions}
            name={props.name}
          />
        ) : null}

 </div>

      {/* ── New inline lookup dropdown — rendered in a portal so it escapes
             any ancestor overflow:hidden/auto (e.g. .mobile-sheet-body) ── */}
      {showInlineDropdown && dropdownPos && createPortal(
        <div
          className="itb-dropdown-portal"
          role="listbox"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          {isLookupLoading && lookupList.length === 0 ? (
            <div className="itb-dropdown-loading">⏳ Searching…</div>
          ) : lookupList.length === 0 ? (
            <div className="itb-dropdown-empty">No results found</div>
          ) : (
            lookupList.map((item, i) => (
              <div
                key={item.code}
                role="option"
                aria-selected={i === activeIdx}
                className={`itb-dropdown-item${i === activeIdx ? " itb-dropdown-item--active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="itb-dropdown-item__code">{item.code}</span>
                <span className="itb-dropdown-item__desc">{item.description}</span>
              </div>
            ))
          )}
        </div>,
        document.body
      )}
    </>
  );
}